<template>
  <div class="select-field-response">
    <div class="field-header">
      <h3 class="field-label">
        {{ field.label }}
        <span v-if="field.required" class="required-indicator">*</span>
        <v-chip x-small color="warning" class="ml-2" v-if="field.useAggregate">Auto-calculated</v-chip>
      </h3>
    </div>

    <div v-if="field.description" class="field-description">
      {{ field.description }}
    </div>

    <v-select
      v-if="field.fieldType === 'SINGLE_SELECT'"
      v-model="localSelectedIds"
      :items="fieldOptions"
      :readonly="disabled || isAggregateField"
      :disabled="isAggregateField"
      item-text="label"
      item-value="id"
      :rules="isAggregateField ? [] : getValidationRules()"
      outlined
      dense
      :placeholder="field.hint || 'Select an option...'"
      return-object
      @change="updateOptionSelection"
    ></v-select>

    <v-select
      v-else
      v-model="localSelectedIds"
      :readonly="disabled || isAggregateField"
      :disabled="isAggregateField"
      :items="fieldOptions"
      item-text="label"
      item-value="id"
      :rules="isAggregateField ? [] : getValidationRules()"
      outlined
      dense
      :placeholder="field.hint || 'Select options...'"
      multiple
      chips
      @change="updateOptionSelection"
    ></v-select>

    <v-textarea
      v-model="localResponse.comment"
      :readonly="disabled"
      outlined
      dense
      label="Comment (Optional)"
      placeholder="Add any additional comments here..."
      rows="2"
      auto-grow
      @change="updateResponse"
    ></v-textarea>
  </div>
</template>

<script >
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { Field } from '@/shared/types/aperTemplate';
import { ResponseDTO } from '@/shared/apis/aper/aperEvaluations';

@Component
export default class SelectFieldResponse extends Vue {
  @Prop({ type: Object, required: true }) field;
  @Prop({ type: Object, required: false }) response;
  @Prop({ type: Boolean, required: false }) disabled;

  localResponse = {
    fieldId: 0,
    selectedOptionIds: [],
    comment: ''
  };

  localSelectedIds = null;

  created() {
    this.initializeResponse();
  }

  @Watch('response')
  onResponseChange() {
    this.initializeResponse();
  }

  initializeResponse() {
    if (this.response) {
      this.localResponse = { ...this.response };

      // Initialize the selected option(s) based on response
      if (this.field.fieldType === 'SINGLE_SELECT') {
        this.localSelectedIds = this.response.selectedOptionIds && this.response.selectedOptionIds.length > 0
          ? this.response.selectedOptionIds[0]
          : null;
      } else {
        this.localSelectedIds = this.response.selectedOptionIds || [];
      }
    } else {
      this.localResponse = {
        fieldId: this.field.id || 0,
        selectedOptionIds: [],
        comment: ''
      };

      // Initialize empty selection
      this.localSelectedIds = this.field.fieldType === 'SINGLE_SELECT' ? null : [];
    }
  }

  get fieldOptions() {
    return [...(this.field.options || [])].sort(
      (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
    );
  }

  get isAggregateField() {
    return this.field.useAggregate === true;
  }

  getValidationRules() {
    const rules = [];

    if (this.field.required) {
      if (this.field.fieldType === 'SINGLE_SELECT') {
        rules.push((v) => v !== null || `${this.field.label} is required`);
      } else {
        rules.push((v) => (v && v.length > 0) || `${this.field.label} is required`);
      }
    }

    return rules;
  }

  updateOptionSelection() {
    if (this.field.fieldType === 'SINGLE_SELECT') {
      this.localResponse.selectedOptionIds = this.localSelectedIds !== null
        ? [this.localSelectedIds]
        : [];
    } else {
      this.localResponse.selectedOptionIds = this.localSelectedIds || [];
    }

    this.updateResponse();
  }

  updateResponse() {
    if(this.localResponse.selectedOptionIds){
      this.localResponse.selectedOptionIds = this.localResponse.selectedOptionIds.map(e => typeof e === "object" ? (e).id : e)
    }
    this.$emit('update:response', this.localResponse);
  }
}
</script>

<style scoped>
.select-field-response {
  width: 100%;
}

.field-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
}

.field-label {
  font-size: 1rem;
  font-weight: 500;
  color: #333;
  margin: 0 0 4px 0;
}

.required-indicator {
  color: #f44336;
  margin-left: 4px;
}

.field-description {
  font-size: 0.85rem;
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: 12px;
}
</style>
